/**
 * Templates Page
 * Browse and use LaTeX templates from disk
 */

import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  DocumentTextIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  FolderIcon,
} from '@heroicons/react/24/outline'
import { latexAPI } from '@api/latex'
import { useDocumentStore } from '@stores/documentStore'
import { logError } from '@utils/logger'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate()
  const { createDocument } = useDocumentStore()

  const { data: templatesData, isLoading, error } = useQuery({
    queryKey: ['latex-templates-disk'],
    queryFn: () => latexAPI.getDiskTemplates(),
  })

  const handleUseTemplate = async (templateName: string) => {
    try {
      const newDoc = await createDocument({
        title: `New Document from ${templateName}`,
        content: '', // Backend will copy template
        document_type: 'latex',
        template_name: templateName, // Specify which template to use
      })
      navigate(`/editor/${newDoc.id}`)
    } catch (error) {
      logError('Failed to create document from template:', error)
    }
  }

  const templates = templatesData?.templates || []

  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
            LaTeX Templates
          </h1>
          <p className="mt-2 text-slate-600 dark:text-slate-400">
            Browse and use pre-built LaTeX templates for your documents
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-slate-600 dark:text-slate-400">Loading templates...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">Failed to load templates</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        )}

        {/* Templates Grid */}
        {!isLoading && !error && (
          <>
            {templates.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="w-16 h-16 text-slate-400 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400">No templates available</p>
                <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                  Templates will appear here once they are added to the system
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template, index) => (
                  <motion.div
                    key={template.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg">
                          <DocumentTextIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                            {template.name}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <FolderIcon className="w-4 h-4 text-slate-400" />
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {template.file_count} files
                            </span>
                          </div>
                        </div>
                      </div>
                      {template.has_main_tex && (
                        <CheckCircleIcon className="w-5 h-5 text-teal-500 flex-shrink-0" />
                      )}
                    </div>

                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                      {template.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {template.has_main_tex && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                            Has main.tex
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleUseTemplate(template.name)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all shadow-sm hover:shadow-md"
                      >
                        <span>Use Template</span>
                        <ArrowRightIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default TemplatesPage

